<!-- eslint-disable vue/no-mutating-props -->
<!-- 管理员、角色、系统参数与支付配置的弹窗模块。表单状态由父页面集中管理。 -->
<template>
  <el-dialog
    :model-value="adminEditorVisible"
    :title="isEditingAdmin ? '编辑管理员' : '新增管理员'"
    width="720px"
    @update:model-value="emit('update:adminEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="登录账号" hint="管理员登录后台时使用的账号。" required>
            <el-input v-model.trim="adminForm.account" placeholder="如 admin" />
          </GuidedField>
          <GuidedField label="管理员昵称" hint="展示在后台右上角和操作日志中。">
            <el-input v-model.trim="adminForm.nickname" placeholder="如 张三" />
          </GuidedField>
          <GuidedField label="登录密码" :hint="isEditingAdmin ? '留空表示保留原密码，不修改。' : '首次创建管理员时必须设置。'" :required="!isEditingAdmin">
            <el-input v-model.trim="adminForm.password" type="password" show-password :placeholder="isEditingAdmin ? '留空不修改' : '请输入登录密码'" />
          </GuidedField>
          <GuidedField label="角色" hint="角色决定管理员可以访问哪些功能。" required>
            <el-select v-model="adminForm.roleIds" multiple @change="emit('adminRoleIdsTouched')">
              <el-option v-for="role in systemRoles" :key="role.id" :label="role.name" :value="role.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="管理门店" hint="所属门店数据范围的角色必须至少选择一家门店。" wide>
            <el-select v-model="adminForm.storeIds" multiple filterable placeholder="请选择可管理门店">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="账号状态" hint="停用后该管理员无法登录后台。">
            <el-select v-model="adminForm.status">
              <el-option label="启用" value="ENABLED" />
              <el-option label="停用" value="DISABLED" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:adminEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="adminSubmitting" :disabled="!adminForm.account" @click="emit('submitAdmin')">
        {{ isEditingAdmin ? '保存修改' : '创建管理员' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="roleEditorVisible"
    :title="isEditingRole ? '编辑角色' : '新增角色'"
    width="720px"
    @update:model-value="emit('update:roleEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="角色名称" hint="展示在管理员配置和权限列表中。" required>
            <el-input v-model.trim="roleForm.name" placeholder="如 门店店长" />
          </GuidedField>
          <GuidedField label="角色编码" hint="系统内部识别角色的唯一编码，建议使用英文大写。">
            <el-input v-model.trim="roleForm.code" placeholder="如 STORE_MANAGER" />
          </GuidedField>
          <GuidedField label="数据范围" hint="限制该角色能查看和操作的数据范围。">
            <el-select v-model="roleForm.dataScope">
              <el-option label="全部数据" value="ALL" />
              <el-option label="所属门店" value="STORE" />
              <el-option label="仅本人" value="SELF" />
            </el-select>
          </GuidedField>
          <GuidedField label="权限" hint="勾选该角色允许使用的后台功能权限。" wide required>
            <div class="permission-tree" @click.stop>
              <div class="permission-tree__all" @click.stop>
                <button
                  type="button"
                  class="permission-option"
                  :class="{ 'is-checked': isAllPermissionSelected }"
                  role="checkbox"
                  :aria-checked="isAllPermissionSelected"
                  @click.stop="handleAllPermissionToggle(!isAllPermissionSelected)"
                >
                  <span class="permission-option__box" />
                </button>
                <span class="permission-option__label">
                  {{ allPermissionOption.label }}
                </span>
              </div>

              <div v-for="module in permissionModules" :key="module.code" class="permission-module" @click.stop>
                <div class="permission-module__header" @click.stop>
                  <div class="permission-module__name" @click.stop>
                    <button
                      type="button"
                      class="permission-option"
                      :class="{
                        'is-checked': module.checked,
                        'is-indeterminate': module.indeterminate,
                      }"
                      role="checkbox"
                      :aria-checked="module.indeterminate ? 'mixed' : module.checked"
                      :disabled="isAllPermissionSelected"
                      @click.stop="handleModuleToggle(module, !module.checked)"
                    >
                      <span class="permission-option__box" />
                    </button>
                    <span class="permission-module__title">{{ module.label }}</span>
                  </div>
                  <button
                    type="button"
                    class="permission-module__toggle"
                    :aria-expanded="module.expanded"
                    @click.stop="toggleModuleExpanded(module)"
                  >
                    <span class="permission-module__count">{{ module.checkedChildren.length }} / {{ module.children.length }}</span>
                    <span class="permission-module__arrow" :class="{ 'is-expanded': module.expanded }">▾</span>
                  </button>
                </div>
                <div v-show="module.expanded" class="permission-module__children" @click.stop>
                  <div
                    v-for="child in module.children"
                    :key="child.value"
                    class="permission-child"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="permission-option"
                      :class="{ 'is-checked': module.checkedChildren.includes(child.value) }"
                      role="checkbox"
                      :aria-checked="module.checkedChildren.includes(child.value)"
                      :disabled="isAllPermissionSelected"
                      @click.stop="handleChildToggle(module, child, !module.checkedChildren.includes(child.value))"
                    >
                      <span class="permission-option__box" />
                    </button>
                    <span class="permission-option__label">{{ child.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:roleEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="roleSubmitting" :disabled="!roleForm.name" @click="emit('submitRole')">
        {{ isEditingRole ? '保存修改' : '创建角色' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="systemParamEditorVisible"
    :title="isEditingSystemParam ? '编辑系统参数' : '新增系统参数'"
    width="680px"
    @update:model-value="emit('update:systemParamEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="参数编码" hint="系统内部读取参数时使用；编辑时不可修改。" required>
            <el-input v-model.trim="systemParamForm.key" :disabled="isEditingSystemParam" placeholder="如 order.cancel_timeout" />
          </GuidedField>
          <GuidedField label="参数分组" hint="用于后台归类管理，选填。">
            <el-input v-model.trim="systemParamForm.groupName" placeholder="如 订单配置" />
          </GuidedField>
          <GuidedField label="参数值" hint="可填写普通文本或 JSON，具体格式取决于参数编码。" wide required>
            <el-input v-model="systemParamForm.value" type="textarea" :rows="4" placeholder='如 true 或 {"enabled":true}' />
          </GuidedField>
          <GuidedField label="参数说明" hint="说明这个参数影响什么功能，方便后续维护。" wide>
            <el-input v-model.trim="systemParamForm.remark" placeholder="如 订单自动取消时间" />
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:systemParamEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="systemParamsSubmitting" :disabled="!systemParamForm.key" @click="emit('submitSystemParam')">
        {{ isEditingSystemParam ? '保存修改' : '创建参数' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="paymentConfigEditorVisible"
    :title="isEditingPaymentConfig ? '编辑支付配置' : '新增支付配置'"
    width="760px"
    @update:model-value="emit('update:paymentConfigEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="支付渠道编码" hint="支付服务的内部编码，例如 WECHAT。" required>
            <el-input v-model.trim="paymentConfigForm.channel" placeholder="如 WECHAT" />
          </GuidedField>
          <GuidedField label="应用 ID" hint="微信小程序或应用的 AppID；编辑时留空表示不修改。">
            <el-input v-model.trim="paymentConfigForm.appId" placeholder="请输入 AppID" />
          </GuidedField>
          <GuidedField label="商户号" hint="支付平台分配的商户编号；编辑时留空表示不修改。">
            <el-input v-model.trim="paymentConfigForm.mchId" placeholder="请输入商户号" />
          </GuidedField>
          <GuidedField label="支付回调地址" hint="支付完成后，支付平台通知后台的接口地址。" wide>
            <el-input v-model.trim="paymentConfigForm.notifyUrl" placeholder="https://example.com/payment/notify" />
          </GuidedField>
          <GuidedField label="API 密钥" hint="敏感信息，仅在新增或需要更新时填写。">
            <el-input v-model.trim="paymentConfigForm.apiKey" type="password" show-password placeholder="留空不修改" />
          </GuidedField>
          <GuidedField label="API V3 密钥" hint="微信支付 V3 使用的敏感密钥，仅在需要更新时填写。">
            <el-input v-model.trim="paymentConfigForm.apiV3Key" type="password" show-password placeholder="留空不修改" />
          </GuidedField>
          <GuidedField label="配置状态" hint="停用后该支付渠道不会被用于新订单。">
            <el-select v-model="paymentConfigForm.enabled">
              <el-option label="启用" :value="true" />
              <el-option label="停用" :value="false" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:paymentConfigEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="paymentConfigSubmitting" :disabled="!paymentConfigForm.channel" @click="emit('submitPaymentConfig')">
        {{ isEditingPaymentConfig ? '保存修改' : '创建配置' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import { Permission } from '@jiaofa/constants'
import GuidedField from '@/components/GuidedField.vue'
import { expandImplicitPermissions } from '@/utils/permissions'
import { pagePermissionModules } from '@/data/pagePermissions'
import type { AdminStore } from '@/api/store'
import type {
  AdminRole,
  AdminSavePayload,
  PaymentConfigSavePayload,
  RoleSavePayload,
  SystemParamSavePayload,
} from '@/api/system'

const props = defineProps<{
  adminEditorVisible: boolean
  roleEditorVisible: boolean
  systemParamEditorVisible: boolean
  paymentConfigEditorVisible: boolean
  adminSubmitting: boolean
  roleSubmitting: boolean
  systemParamsSubmitting: boolean
  paymentConfigSubmitting: boolean
  adminForm: AdminSavePayload
  roleForm: RoleSavePayload
  systemParamForm: SystemParamSavePayload
  paymentConfigForm: PaymentConfigSavePayload
  systemRoles: AdminRole[]
  storeOptions: AdminStore[]
}>()

const emit = defineEmits<{
  'update:adminEditorVisible': [value: boolean]
  'update:roleEditorVisible': [value: boolean]
  'update:systemParamEditorVisible': [value: boolean]
  'update:paymentConfigEditorVisible': [value: boolean]
  adminRoleIdsTouched: []
  submitAdmin: []
  submitRole: []
  submitSystemParam: []
  submitPaymentConfig: []
}>()

const isEditingAdmin = computed(() => Boolean(props.adminForm.id))
const isEditingRole = computed(() => Boolean(props.roleForm.id))
const isEditingSystemParam = computed(() => Boolean(props.systemParamForm.id))
const isEditingPaymentConfig = computed(() => Boolean(props.paymentConfigForm.id))

interface PermissionOption {
  label: string
  value: string
}

interface PermissionModule {
  code: string
  label: string
  children: PermissionOption[]
  expanded: boolean
}

const allPermissionOption: PermissionOption = { label: '全部权限', value: Permission.All }

/**
 * 页面级权限目录（本地清单，阶段 A 前端先行）。
 * 后端 `sys_permission` 对齐到页面级后，可改回由 `GET /system/permissions` 下发。
 */
const permissionModulesSource = computed(() =>
  pagePermissionModules.map((module) => ({
    code: module.code,
    label: module.label,
    children: module.children.map((child) => ({ label: child.name, value: child.code })),
  })),
)

const isAllPermissionSelected = computed(() => (props.roleForm.permissions ?? []).includes(Permission.All))

/** 每个模块的运行时状态：展开态 + 已勾选子权限。随 roleForm.permissions 派生。 */
const moduleStates = reactive<Record<string, { expanded: boolean; checkedChildren: string[] }>>({})

const permissionModules = computed<Array<PermissionModule & { checked: boolean; indeterminate: boolean; checkedChildren: string[] }>>(() => {
  const selected = new Set(props.roleForm.permissions ?? [])
  return permissionModulesSource.value.map((module) => {
    const state = moduleStates[module.code] ?? (moduleStates[module.code] = { expanded: false, checkedChildren: [] })
    const checkedChildren = module.children.filter((child) => selected.has(child.value)).map((child) => child.value)
    state.checkedChildren = checkedChildren
    const checkedCount = checkedChildren.length
    const total = module.children.length
    return {
      ...module,
      expanded: state.expanded,
      checked: checkedCount === total,
      indeterminate: checkedCount > 0 && checkedCount < total,
      checkedChildren,
    }
  })
})

function syncRoleFormPermissions(permissions: string[]): void {
  updateRoleFormPermissions(props.roleForm, expandImplicitPermissions(permissions))
}

function handleAllPermissionToggle(value: boolean): void {
  if (value) {
    updateRoleFormPermissions(props.roleForm, [Permission.All])
  } else {
    updateRoleFormPermissions(props.roleForm, [])
  }
}

function handleModuleToggle(module: { code: string; children: PermissionOption[] }, value: boolean): void {
  const selected = new Set(props.roleForm.permissions ?? [])
  const childValues = module.children.map((child) => child.value)
  childValues.forEach((childValue) => {
    if (value) selected.add(childValue)
    else selected.delete(childValue)
  })
  const state = moduleStates[module.code] ?? (moduleStates[module.code] = { expanded: true, checkedChildren: [] })
  state.expanded = true
  syncRoleFormPermissions([...selected])
}

function handleChildToggle(module: { code: string }, child: PermissionOption, value: boolean): void {
  const selected = new Set(props.roleForm.permissions ?? [])
  if (value) selected.add(child.value)
  else selected.delete(child.value)
  syncRoleFormPermissions([...selected])
}

function toggleModuleExpanded(module: { code: string }): void {
  const state = moduleStates[module.code] ?? (moduleStates[module.code] = { expanded: false, checkedChildren: [] })
  state.expanded = !state.expanded
}

function updateRoleFormPermissions(roleForm: RoleSavePayload, permissions: string[]): void {
  Object.assign(roleForm, {
    permissions,
  })
}
</script>

<style scoped lang="scss">
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.detail-grid--single { grid-template-columns: 1fr; }
.detail-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; }
.detail-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.permission-tree { display: flex; flex-direction: column; gap: 8px; }
.permission-tree__all { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border: 1px solid var(--jf-color-border); border-radius: 8px; }
.permission-module { border: 1px solid var(--jf-color-border); border-radius: 8px; }
.permission-module__header { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; }
.permission-module__name,
.permission-child { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.permission-module__title { font-weight: 600; }
.permission-module__toggle { display: inline-flex; align-items: center; gap: 8px; border: none; background: transparent; padding: 0; cursor: pointer; color: var(--jf-color-text-secondary); font-size: 12px; }
.permission-module__count { color: var(--jf-color-text-secondary); }
.permission-module__arrow { display: inline-block; transition: transform 0.2s ease; font-size: 12px; }
.permission-module__arrow.is-expanded { transform: rotate(180deg); }
.permission-module__children { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; padding: 0 12px 12px 32px; }
.permission-option { width: 16px; height: 16px; flex: 0 0 16px; padding: 0; border: 0; background: transparent; cursor: pointer; }
.permission-option:disabled { cursor: not-allowed; opacity: 0.5; }
.permission-option__box { position: relative; display: block; width: 16px; height: 16px; box-sizing: border-box; border: 1px solid var(--jf-color-border); border-radius: 3px; background: #fff; }
.permission-option.is-checked .permission-option__box { border-color: var(--jf-color-primary); background: var(--jf-color-primary); }
.permission-option.is-checked .permission-option__box::after { position: absolute; left: 4px; top: 1px; width: 5px; height: 9px; border: 2px solid #fff; border-top: 0; border-left: 0; content: ''; transform: rotate(45deg); }
.permission-option.is-indeterminate .permission-option__box { border-color: var(--jf-color-primary); background: var(--jf-color-primary); }
.permission-option.is-indeterminate .permission-option__box::after { position: absolute; left: 3px; right: 3px; top: 7px; height: 2px; background: #fff; content: ''; }
.permission-option__label { color: var(--jf-color-text-primary); }
@media (max-width: 760px) {
  .detail-grid, .detail-actions, .permission-module__children { grid-template-columns: 1fr; }
}
</style>
